import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import * as _ from 'lodash-es';
import { FirRepointService } from './fir-repoint.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

interface RepointItem {
  SYSC_ACCT?: string;
  SYS_ACCT_KEY?: string;
  TRANS_HEADER_KEY?: string | number;
  TRANS_DETAILS_KEY?: string | number;
  IRS_Form: string;
  IRS_SCHEDULE: string;
  IRS_LINE: string;
  IRS_LINE_DESC: string;
  CHECK_COL: string | null;
  LOOK_UP_BTN?: any;
  AMMOUNT: number | string;
  MAIN_ACCT_Y_N: string;
  REPOINT_ACCT_KEY?: number;
  DISABLED: boolean;
  DELETE_BTN: boolean;
  GROUP_KEY: number;
  loadLookupSchedule?: string[];
  loadLookupLineDesc?: string[];
  loadLookupColumn?: string[];
  acctzeroflag?: boolean;
  irsFormFlag?: boolean;
  irsScheduleFlag?: boolean;
  irsColFlag?: boolean;
  TYPE?: string;
}

interface SystemLocalAccountData {
  ROW_ORDER?: string;
  SYS_ACCT_KEY?: string;
  FORMS?: string;
  FORM: string;
  SCHEDULE: string;
  LINE: string;
  LINE_DESC: string;
  COL: string;
  FROM_CHART_KEY?: any;
}

@Component({
    selector: 'gtw-fir-repoint',
    templateUrl: './fir-repoint.component.html',
    styleUrls: ['./fir-repoint.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirRepointComponent implements OnInit, OnDestroy {
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly contextSubs = new Subscription();
  private contextEpoch = 0;
  private unsubscribe$ = new Subject<void>();

  // Prevent duplicate initialization
  private isInitialized = false;
  private isLoading = false;
  /** Bumped on each load batch and `reset()` refetch; stale HTTP callbacks no-op. */
  private loadEpoch = 0;

  // Modal state
  modalTitle = "Trial Balance Repointing";
  minimise = false;
  crudLoading = false;
  loading = false;
  loadLookUpData = false;

  // Data properties
  rowData: any = {};
  curr = '';
  curr5471 = '';
  curr8858: string | null = null;

  // Display properties
  ACCT_TYPE = '';
  CHART_DISPLAY = '';
  LOCAL_ACCT_DISPLAY = '';
  SYSTEM_ACC_DISPLAY = '';

  // Amount properties
  cyAmount = 0;
  elimAmount = 0;
  repointingAmount = 0;
  remainingCyAmount = 0;
  adjAmountFeed = 0;
  adjAmount5471 = 0;
  adjAmount8858 = 0;
  pyAmount = 0;

  // Repoint data
  Repoint: any = {
    SYSC_ACCT: '',
    SYS_ACCT_KEY: '',
    TRANS_HEADER_KEY: '',
    TRANS_DETAILS_KEY: '',
    IRS_Form: '',
    IRS_SCHEDULE: '',
    CHECK_COL: '',
    IRS_LINE: '',
    IRS_LINE_DESC: '',
    CHECK_SYS_MAP_ACCT: '',
    LOOK_UP_BTN: '',
    AMMOUNT: '',
    newadded: '',
    ADJUSTMENT_DESC: ''
  };
  OLD_DESC = '';

  // Lists and lookups
  repointList: RepointItem[] = [];
  tempDeletedRepointList: any[] = [];
  loadSystemAndLocalAccountData: SystemLocalAccountData[] = [];
  sys_acc_lookup_data: any[] = [];
  repoint_data: any[] = [];
  loadLookupForm: string[] = [];
  sendobj: any[] = [];

  // State flags
  showRepointingError = false;
  showRepointingDescError = false;
  acctzeroflag = false;
  irsFormFlag = false;
  irsScheduleFlag = false;
  addRepointClicked = false;
  is_data_saved = false;
  group_key = 0;

  // Sorting
  orderByField = '';
  reverseSort: boolean | string = false;
  sort_key: { [key: string]: number } = {
    '1': 0,
    '2': 0,
    '3': 0,
    '4': 0,
    '5': 0
  };

  // Sorted data for display (getter for template)
  get sortedSystemAndLocalAccountData(): SystemLocalAccountData[] {
    if (!this.loadSystemAndLocalAccountData || this.loadSystemAndLocalAccountData.length === 0) {
      return [];
    }
    
    // If no sorting is applied, return original data
    if (!this.orderByField || this.reverseSort === '') {
      return this.loadSystemAndLocalAccountData;
    }
    
    // Create a copy for sorting
    const sortedData = [...this.loadSystemAndLocalAccountData];
    
    // Sort based on orderByField
    sortedData.sort((a: any, b: any) => {
      const valA = a[this.orderByField] || '';
      const valB = b[this.orderByField] || '';
      
      // String comparison
      let comparison = 0;
      if (valA < valB) {
        comparison = -1;
      } else if (valA > valB) {
        comparison = 1;
      }
      
      // Reverse if needed
      return this.reverseSort === true ? -comparison : comparison;
    });
    
    return sortedData;
  }

  // Global configuration
  GENERAL_CONFIG: any = {};
  baseURLs: any = {};
  globalService: any = {};
  filterParams: any = {};

  constructor(
    private firRepointService: FirRepointService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.resetAndTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.resetAndTryInit();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.resetAndTryInit();
      })
    );
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
  }

  private resetAndTryInit(): void {
    this.contextEpoch++;
    this.loadEpoch++;
    this.isInitialized = false;
    this.isLoading = false;
    this.applyWorkspaceContext();
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit() {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    const baseUrl = this.baseURLs?.api || this.GENERAL_CONFIG?.base_url || this.GENERAL_CONFIG?.api || '';

    if (this.rowData && Object.keys(this.rowData).length > 0 && baseUrl) {
      if (!this.baseURLs?.api && baseUrl) {
        this.baseURLs = { ...this.baseURLs, api: baseUrl };
      }

      this.isInitialized = true;
      this.init();
    }
  }

  private resetComponentState(): void {
    this.Repoint = {
      SYSC_ACCT: '', SYS_ACCT_KEY: '', TRANS_HEADER_KEY: '', TRANS_DETAILS_KEY: '',
      IRS_Form: '', IRS_SCHEDULE: '', CHECK_COL: '', IRS_LINE: '', IRS_LINE_DESC: '',
      CHECK_SYS_MAP_ACCT: '', LOOK_UP_BTN: '', AMMOUNT: '', newadded: '', ADJUSTMENT_DESC: ''
    };
    this.OLD_DESC = '';
    this.repointList = [];
    this.tempDeletedRepointList = [];
    this.loadSystemAndLocalAccountData = [];
    this.sys_acc_lookup_data = [];
    this.repoint_data = [];
    this.loadLookupForm = [];
    this.sendobj = [];

    this.showRepointingError = false;
    this.showRepointingDescError = false;
    this.acctzeroflag = false;
    this.irsFormFlag = false;
    this.irsScheduleFlag = false;
    this.addRepointClicked = false;
    this.is_data_saved = false;
    this.group_key = 0;

    this.repointingAmount = 0;
    this.loadLookUpData = false;

    this.orderByField = '';
    this.reverseSort = false;
    this.sort_key = { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 };
  }

  init() {
    this.crudLoading = false;
    this.resetComponentState();
    this.setupDisplayData();
    this.loadAllData();
  }

  private setupDisplayData() {
    // Setup display properties from rowData
    this.curr = this.rowData.FC || '';
    this.curr5471 = this.rowData.HO_CURR || '';
    this.curr8858 = this.rowData.CURR_8858 !== undefined ? this.rowData.CURR_8858 : null;

    this.ACCT_TYPE = this.rowData.ACCT_TYPE || '';
    this.CHART_DISPLAY = this.rowData.LOCAL_CHART_CODE || '';
    this.LOCAL_ACCT_DISPLAY = this.rowData.LOCAL_ACCT || '';
    this.SYSTEM_ACC_DISPLAY = this.rowData.SYSTEM_ACCT || '';

    // Setup amounts
    this.cyAmount = this.rowData.TAX_AMT_FEED_CURR || 0;
    this.elimAmount = this.rowData.ELIM_AMT_FEED_CURR || 0;
    this.adjAmountFeed = this.rowData.ADJUSTED_TAX_AMT_FEED_CURR || 0;
    this.adjAmount5471 = this.rowData.ADJUSTED_5471_TAX_AMT || 0;
    this.adjAmount8858 = this.rowData.ADJUSTED_8858_TAX_AMT || 0;
    this.pyAmount = this.rowData.BOY_AMT || 0;
    this.remainingCyAmount = this.rowData.ADJUSTED_TAX_AMT_FEED_CURR || 0;

    // Pre-fill ADJUSTMENT_DESC from rowData if available (grid may pass it)
    if (this.rowData.ADJUSTMENT_DESC && !this.Repoint.ADJUSTMENT_DESC) {
      this.Repoint.ADJUSTMENT_DESC = this.rowData.ADJUSTMENT_DESC;
      this.OLD_DESC = this.rowData.ADJUSTMENT_DESC;
    }

    this.cdr.detectChanges();
  }

  private loadAllData() {
    this.isLoading = true;
    const epoch = ++this.loadEpoch;
    this.getSystemAcctLookup(epoch);
    this.fetchSystemAcctData(epoch);
    this.getDefaultSystemAcctValues(epoch);
  }

  // Sorting methods
  sortBy(key: string, value: number) {
    // Reset all other sort keys to 0 (no sort)
    Object.keys(this.sort_key).forEach(k => {
      if (k !== key) {
        this.sort_key[k] = 0;
      }
    });

    switch (value) {
      case 0:
        this.sort_key[key] = 1;
        this.reverseSort = false;
        break;
      case 1:
        this.sort_key[key] = 2;
        this.reverseSort = true;
        break;
      case 2:
        this.sort_key[key] = 0;
        this.reverseSort = '';
        this.orderByField = ''; // Clear orderByField when no sorting
        break;
    }
    this.cdr.detectChanges();
  }

  // Adjustment description change handler
  adjDescChanged() {
    if (this.repointList.length > 0 && (!this.Repoint.ADJUSTMENT_DESC || this.Repoint.ADJUSTMENT_DESC === '')) {
      this.showRepointingDescError = true;
    } else {
      this.showRepointingDescError = false;
    }
    this.cdr.detectChanges();
    return this.showRepointingDescError;
  }

  // Add new repoint entry
  addRepoint() {
    this.addRepointClicked = true;

    let tempTransHeader_key: string | number = 0;
    for (let i = 0; i < this.repointList.length; i++) {
      if (this.repointList[i].TRANS_HEADER_KEY && Number(this.repointList[i].TRANS_HEADER_KEY) > 0) {
        tempTransHeader_key = this.repointList[i].TRANS_HEADER_KEY!;
        break;
      }
    }

    this.group_key = this.group_key + 1;
    const lookupData = _.cloneDeep(this.sys_acc_lookup_data);

    for (let i = 0; i < this.loadSystemAndLocalAccountData.length; i++) {
      const item = this.loadSystemAndLocalAccountData[i];
      const colParts = item.COL
        ? item.COL.split(',').map((c: string) => c.trim()).filter((c: string) => c.length > 0)
        : [];
      const lineDescKey = item.LINE + " - " + item.LINE_DESC;

      // Default the Column to whatever the underlying account row carries.
      // For single-value cols ("FC", "N/A", ...) this is the obvious choice.
      // For comma-joined cols ("BOY,EOY", "B,P", "FC,US") legacy stored
      // `null` here, which left the dropdown blank and forced the user to
      // pick - but the value DOES come from the payload, and `buildColumnOptions`
      // surfaces the literal as its own selectable option, so we now
      // pre-select it for visibility. Users can still narrow it down to a
      // single split value via the dropdown.
      const initialCheckCol = item.COL ? item.COL : (colParts[0] || '');
      const lookupCols = this.filterDataByKey("LINE_DESC", 'COL', lookupData, lineDescKey, item.SCHEDULE);
      const mergedCols = this.buildColumnOptions(lookupCols, [item.COL], initialCheckCol);

      this.repointList.push({
        IRS_Form: item.FORM,
        IRS_SCHEDULE: item.SCHEDULE,
        IRS_LINE: item.LINE,
        IRS_LINE_DESC: lineDescKey,
        CHECK_COL: initialCheckCol,
        LOOK_UP_BTN: null,
        AMMOUNT: this.Repoint.AMMOUNT || '',
        MAIN_ACCT_Y_N: 'Y',
        REPOINT_ACCT_KEY: 0,
        DISABLED: false,
        DELETE_BTN: i === 0,
        GROUP_KEY: this.group_key,
        TRANS_HEADER_KEY: tempTransHeader_key,
        loadLookupSchedule: this.filterDataByKey("FORM", 'SCHEDULE', lookupData, item.FORM, ''),
        loadLookupLineDesc: this.filterDataByKey("SCHEDULE", 'LINE_DESC', lookupData, item.SCHEDULE, item.FORM),
        loadLookupColumn: mergedCols
      });
    }

    this.cdr.detectChanges();
  }

  // Remove repoint entry
  removeRepointAmount(index: number, group_key: number) {
    if (this.sendobj.length > index) {
      this.sendobj[index].TYPE = "D";
      this.tempDeletedRepointList.push(this.sendobj[index]);
      this.sendobj.splice(index, 1);
    }
    this.repointList.splice(index, 1);

    // Remove all entries with same group_key
    let i = 0;
    while (i < this.sendobj.length) {
      if (this.sendobj[i].GROUP_KEY === group_key) {
        this.sendobj[i].TYPE = "D";
        this.sendobj.splice(i, 1);
      } else {
        i++;
      }
    }

    let j = 0;
    while (j < this.repointList.length) {
      if (this.repointList[j].GROUP_KEY === group_key) {
        this.repointList[j].TYPE = "D";
        this.repointList.splice(j, 1);
      } else {
        j++;
      }
    }

    this.repointAmt();
    this.adjDescChanged();
    this.cdr.detectChanges();
  }

  // Calculate repoint amounts
  repointAmt() {
    this.repointingAmount = 0;
    this.acctzeroflag = false;
    let temp_group_key: number | null = null;

    this.repointList.forEach((repoint) => {
      if (repoint.AMMOUNT != null && repoint.AMMOUNT !== '' && repoint.GROUP_KEY !== temp_group_key) {
        const x = parseInt(String(repoint.AMMOUNT), 10);
        this.repointingAmount = this.repointingAmount + x;
        temp_group_key = repoint.GROUP_KEY;
      }
    });

    this.remainingCyAmount = this.cyAmount + this.elimAmount - this.repointingAmount;
    this.adjAmount5471 = (this.cyAmount * (this.rowData.HO_FX_RATE || 0)) + (this.rowData.HO_ELIM_AMT || 0) - (this.repointingAmount * (this.rowData.HO_FX_RATE || 0));
    this.adjAmount8858 = this.rowData.CURR_8858 === undefined ? 0 : (this.cyAmount - this.repointingAmount) * (this.rowData.FX_RATE_8858 || 0);

    // Check for zero amounts
    for (const repoint of this.repointList) {
      repoint.acctzeroflag = false;
      if (repoint.AMMOUNT === 0 || repoint.AMMOUNT === undefined || repoint.AMMOUNT === 'NaN' || repoint.AMMOUNT === '') {
        repoint.acctzeroflag = true;
        this.acctzeroflag = true;
      }
    }

    this.cdr.detectChanges();
  }

  // Update amount for a group
  updateAmt(group_key: number, amt: number | string) {
    const filterData = this.repointList.filter(obj => obj.GROUP_KEY === group_key);
    filterData.forEach(obj => {
      obj.AMMOUNT = amt;
    });
    this.repointAmt();
  }

  // Line change handler
  changeLine(repointObj: RepointItem) {
    if (repointObj.IRS_LINE_DESC) {
      repointObj.IRS_LINE = repointObj.IRS_LINE_DESC.split("-")[0].replace(/^\s+|\s+$/g, '');
    }
    this.cdr.detectChanges();
  }

  // Get lookup data for next dropdown level
  getLookupNext(key: number, nextLookupKey: string, repointObj: RepointItem) {
    const lookupData = _.cloneDeep(this.sys_acc_lookup_data);

    switch (key) {
      case 1:
        repointObj.loadLookupSchedule = this.filterDataByKey("FORM", nextLookupKey, lookupData, repointObj.IRS_Form, '');
        break;
      case 2:
        repointObj.loadLookupLineDesc = this.filterDataByKey("SCHEDULE", nextLookupKey, lookupData, repointObj.IRS_SCHEDULE, repointObj.IRS_Form);
        break;
      case 3: {
        const lookupCols = this.filterDataByKey("LINE_DESC", nextLookupKey, lookupData, repointObj.IRS_LINE_DESC, repointObj.IRS_SCHEDULE);
        const accountCols = this.collectAccountColsForRepoint(repointObj);
        repointObj.loadLookupColumn = this.buildColumnOptions(lookupCols, accountCols, repointObj.CHECK_COL);
        break;
      }
      case 4:
        // loadLookupSysMapAcct - not used in current implementation
        break;
    }
    this.cdr.detectChanges();
  }

  // Filter data for dropdown options
  private filterDataByKey(currentLookupKey: string, nextLookupKey: string, lookupData: any[], currentLookupValue: string, preLookUpValue: string): string[] {
    let nextLookupData: string[] = [];

    const currentLookupFilter = lookupData.filter((object) => {
      if (nextLookupKey === 'SCHEDULE') {
        const uniqueSched: string[] = [];
        for (let k = 0; k < this.loadSystemAndLocalAccountData.length; k++) {
          if (this.loadSystemAndLocalAccountData[k].FORM === currentLookupValue) {
            uniqueSched.push(this.loadSystemAndLocalAccountData[k].SCHEDULE);
          }
        }
        return object[currentLookupKey] === currentLookupValue && _.indexOf(uniqueSched, object['SCHEDULE']) !== -1;
      } else if (nextLookupKey === 'LINE_DESC') {
        return object[currentLookupKey] === currentLookupValue && object['FORM'] === preLookUpValue;
      } else if (nextLookupKey === 'COL') {
        return object[currentLookupKey] === currentLookupValue && object['SCHEDULE'] === preLookUpValue;
      } else {
        return object[currentLookupKey] === currentLookupValue;
      }
    });

    currentLookupFilter.forEach((object) => {
      const raw = object[nextLookupKey];
      // COL values in sys_acc_lookup_data are frequently stored as a single
      // comma-separated string (e.g. "FC,US"). Native <select> cannot render a
      // comma-separated literal as multiple selectable options, so we split
      // those here and drop blanks.
      if (nextLookupKey === 'COL' && typeof raw === 'string' && raw.indexOf(',') !== -1) {
        raw.split(',').forEach((part: string) => {
          const trimmed = part.trim();
          if (trimmed) {
            nextLookupData.push(trimmed);
          }
        });
      } else if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
        nextLookupData.push(String(raw).trim());
      }
    });

    nextLookupData = _.uniq(nextLookupData);

    if (nextLookupKey === 'LINE_DESC') {
      // Sort by line number
      nextLookupData = _.sortBy(nextLookupData, (line: string) => {
        const match = line.split("-")[0].match(/(\d+)/);
        return match ? parseInt(match[0]) : 0;
      });
    }

    return nextLookupData;
  }

  /**
   * Build the Column dropdown options for a repoint row, merging the IRS lookup
   * result with the jurisdiction codes that already live on the underlying
   * account row (`loadSystemAndLocalAccountData`). For some tax years the IRS
   * lookup dataset returns an empty/placeholder COL for a given LINE_DESC even
   * though the account itself carries the real codes ("FC,US"). Legacy code
   * only consumed the lookup and the dropdown silently rendered blank.
   *
   * We also always preserve the currently selected `CHECK_COL` as a visible
   * option so that a saved value never appears as an empty dropdown.
   */
  private buildColumnOptions(
    lookupCols: string[] | undefined,
    accountColSources: Array<string | undefined | null>,
    currentCheckCol?: string | null
  ): string[] {
    const result: string[] = [];

    const pushParts = (raw: any) => {
      if (raw === undefined || raw === null) {
        return;
      }
      String(raw)
        .split(',')
        .map((c: string) => c.trim())
        .filter((c: string) => c.length > 0)
        .forEach((part: string) => {
          if (!result.includes(part)) {
            result.push(part);
          }
        });
    };

    if (Array.isArray(lookupCols)) {
      lookupCols.forEach(pushParts);
    }

    accountColSources.forEach(pushParts);

    if (currentCheckCol) {
      pushParts(currentCheckCol);
    }

    // Saved COL is sometimes a comma-joined token (e.g. "BOY,EOY",
    // "B,P", "FC,US") that does not match any of the split single-value
    // options. Without this safety net the native <select> cannot find
    // its bound value and renders blank, even though the value came back
    // from the API. Keep the literal as its own selectable option so the
    // dropdown can show the saved value as selected and users can still
    // pick a single split option to narrow it down.
    if (currentCheckCol) {
      const literal = String(currentCheckCol).trim();
      if (literal && literal.indexOf(',') !== -1 && !result.includes(literal)) {
        result.push(literal);
      }
    }

    return result;
  }

  /**
   * Collect every account-level COL string whose FORM/SCHEDULE/LINE matches the
   * repoint entry. Falls back to looser matches so saved repoints whose LINE
   * differs from the current mapping still surface their codes.
   */
  private collectAccountColsForRepoint(repoint: RepointItem): string[] {
    if (!this.loadSystemAndLocalAccountData || this.loadSystemAndLocalAccountData.length === 0) {
      return [];
    }

    const form = repoint.IRS_Form;
    const schedule = repoint.IRS_SCHEDULE;
    const line = String(repoint.IRS_LINE || '');

    const exactMatches = this.loadSystemAndLocalAccountData.filter((acc: SystemLocalAccountData) =>
      acc.FORM === form &&
      acc.SCHEDULE === schedule &&
      String(acc.LINE || '') === line &&
      !!acc.COL
    );
    if (exactMatches.length > 0) {
      return exactMatches.map((acc: SystemLocalAccountData) => acc.COL);
    }

    const formScheduleMatches = this.loadSystemAndLocalAccountData.filter((acc: SystemLocalAccountData) =>
      acc.FORM === form && acc.SCHEDULE === schedule && !!acc.COL
    );
    if (formScheduleMatches.length > 0) {
      return formScheduleMatches.map((acc: SystemLocalAccountData) => acc.COL);
    }

    const formMatches = this.loadSystemAndLocalAccountData.filter((acc: SystemLocalAccountData) =>
      acc.FORM === form && !!acc.COL
    );
    return formMatches.map((acc: SystemLocalAccountData) => acc.COL);
  }

  // Validate form data before save
  changeFormData() {
    this.irsFormFlag = false;
    this.irsScheduleFlag = false;

    for (const repoint of this.repointList) {
      if (repoint.IRS_Form === "" || repoint.IRS_Form === undefined) {
        repoint.irsFormFlag = true;
        this.irsFormFlag = true;
      } else {
        repoint.irsFormFlag = false;
      }

      if (repoint.IRS_SCHEDULE === "" || repoint.IRS_SCHEDULE === undefined) {
        repoint.irsScheduleFlag = true;
        this.irsScheduleFlag = true;
      } else {
        repoint.irsScheduleFlag = false;
      }

      // Column validation for specific schedules
      if (repoint.IRS_SCHEDULE === 'M' && repoint.IRS_Form === '5471' && repoint.IRS_LINE !== "" &&
          (!repoint.CHECK_COL || repoint.CHECK_COL === '')) {
        repoint.irsColFlag = true;
        this.irsFormFlag = true;
      } else if (repoint.IRS_SCHEDULE === 'M' && repoint.IRS_Form === '8858' && repoint.IRS_LINE !== "" &&
          (!repoint.CHECK_COL || repoint.CHECK_COL === '')) {
        repoint.irsColFlag = true;
        this.irsFormFlag = true;
      } else if (repoint.IRS_SCHEDULE === 'N' && repoint.IRS_Form === '8865' && repoint.IRS_LINE !== "" &&
          (!repoint.CHECK_COL || repoint.CHECK_COL === '')) {
        repoint.irsColFlag = true;
        this.irsFormFlag = true;
      } else {
        repoint.irsColFlag = false;
      }
    }
  }

  // Add to JSON for save
  private addToJson(repointObj: any, isDefaultRecord: boolean) {
    const returnObjD: any = {};

    returnObjD["TYPE"] = isDefaultRecord ? 'E' : 'I';
    returnObjD['COMBINATION_KEY'] = this.rowData.COMBINATION_KEY;
    returnObjD['LOCAL_CHART_KEY'] = this.rowData.LOCAL_CHART_KEY;
    returnObjD['LOCAL_ACCT'] = this.rowData.LOCAL_ACCT;
    returnObjD['NEW_REPOINT_FORM'] = repointObj.IRS_Form;
    returnObjD['NEW_REPOINT_SCHEDULE'] = repointObj.IRS_SCHEDULE;
    returnObjD['NEW_REPOINT_LINE'] = repointObj.IRS_LINE;
    returnObjD['NEW_REPOINT_COL'] = repointObj.CHECK_COL === 'N/A' ? null : repointObj.CHECK_COL;
    returnObjD['OLD_REPOINT_FORM'] = repointObj.OLD_REPOINT_FORM !== undefined ? repointObj.OLD_REPOINT_FORM : repointObj.IRS_Form;
    returnObjD['OLD_REPOINT_SCHEDULE'] = repointObj.OLD_REPOINT_SCHEDULE !== undefined ? repointObj.OLD_REPOINT_SCHEDULE : repointObj.IRS_SCHEDULE;
    returnObjD['OLD_REPOINT_LINE'] = repointObj.OLD_REPOINT_LINE !== undefined ? repointObj.OLD_REPOINT_LINE : repointObj.IRS_LINE;
    returnObjD['OLD_REPOINT_COL'] = repointObj.OLD_REPOINT_COL !== undefined ? repointObj.OLD_REPOINT_COL : (repointObj.CHECK_COL === 'N/A' ? null : repointObj.CHECK_COL);
    returnObjD['OLD_ADJ_AMT'] = repointObj.AMMOUNT;
    returnObjD['NEW_ADJ_AMT'] = repointObj.AMMOUNT;
    returnObjD['TRANS_HEADER_KEY'] = repointObj.TRANS_HEADER_KEY !== '' ? repointObj.TRANS_HEADER_KEY : '';
    returnObjD['TRANS_DETAILS_KEY'] = repointObj.TRANS_DETAILS_KEY !== '' ? repointObj.TRANS_DETAILS_KEY : '';
    returnObjD['OLD_ADJUSTMENT_DESC'] = this.OLD_DESC;
    returnObjD['NEW_ADJUSTMENT_DESC'] = this.Repoint.ADJUSTMENT_DESC;
    returnObjD['SOURCE_SYSTEM'] = this.rowData.SOURCE_SYSTEM;
    returnObjD['REPOINT_ACCT_KEY'] = repointObj.REPOINT_ACCT_KEY;
    returnObjD['MAIN_ACCT_Y_N'] = repointObj.MAIN_ACCT_Y_N;
    returnObjD['TP_KEY'] = this.rowData.TP_KEY !== undefined ? this.rowData.TP_KEY : 0;
    returnObjD['GROUP_KEY'] = repointObj.GROUP_KEY;

    this.sendobj.push(returnObjD);
  }

  // Save method
  save() {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    this.changeFormData();
    this.crudLoading = true;
    this.acctzeroflag = false;

    if (this.irsFormFlag || this.irsScheduleFlag) {
      this.gtwSnackbarService.openSnackBar('Please correct the errors below', 'gtw-snackbar--red');
      this.crudLoading = false;
      this.cdr.detectChanges();
      return;
    }

    const finalRepointList = JSON.parse(JSON.stringify(this.repointList));

    // Validate adjustment description (matching original AngularJS: console.log(vm.adjDescChanged()))
    this.adjDescChanged();

    // Validate amounts
    for (const repoint of this.repointList) {
      repoint.acctzeroflag = false;
      if (repoint.AMMOUNT === 0 || repoint.AMMOUNT === undefined || !repoint.AMMOUNT ||
          repoint.AMMOUNT === null || repoint.AMMOUNT === 'NaN') {
        repoint.acctzeroflag = true;
        this.acctzeroflag = true;
      }
    }

    if (!this.showRepointingError && !this.showRepointingDescError && !this.acctzeroflag) {
      for (let i = 0; i < finalRepointList.length; i++) {
        if (this.sendobj.length > i) {
          this.sendobj[i].NEW_ADJUSTMENT_DESC = this.Repoint.ADJUSTMENT_DESC;

          if (this.sendobj[i].TYPE === "E") {
            this.sendobj[i].NEW_REPOINT_FORM = finalRepointList[i].IRS_Form;
            this.sendobj[i].NEW_REPOINT_SCHEDULE = finalRepointList[i].IRS_SCHEDULE;
            this.sendobj[i].NEW_REPOINT_LINE = finalRepointList[i].IRS_LINE;
            this.sendobj[i].NEW_REPOINT_COL = finalRepointList[i].CHECK_COL;
            this.sendobj[i].NEW_ADJ_AMT = finalRepointList[i].AMMOUNT;
            this.sendobj[i].REPOINT_ACCT_KEY = finalRepointList[i].REPOINT_ACCT_KEY;
            this.sendobj[i].GROUP_KEY = finalRepointList[i].GROUP_KEY;

            // Check if line changed
            if (this.sendobj[i].NEW_REPOINT_FORM === this.sendobj[i].OLD_REPOINT_FORM &&
                this.sendobj[i].NEW_REPOINT_SCHEDULE === this.sendobj[i].OLD_REPOINT_SCHEDULE &&
                this.sendobj[i].NEW_REPOINT_LINE === this.sendobj[i].OLD_REPOINT_LINE) {
              this.sendobj[i].LINE_CHANGE = this.sendobj[i].NEW_REPOINT_COL === this.sendobj[i].OLD_REPOINT_COL ? 'N' : 'Y';
            } else {
              this.sendobj[i].LINE_CHANGE = 'Y';
            }

            // Remove unchanged entries
            if (this.sendobj[i].OLD_REPOINT_FORM === this.sendobj[i].NEW_REPOINT_FORM &&
                this.sendobj[i].OLD_REPOINT_SCHEDULE === this.sendobj[i].NEW_REPOINT_SCHEDULE &&
                this.sendobj[i].OLD_REPOINT_LINE === this.sendobj[i].NEW_REPOINT_LINE &&
                this.sendobj[i].OLD_REPOINT_COL === this.sendobj[i].NEW_REPOINT_COL &&
                this.sendobj[i].OLD_ADJ_AMT === this.sendobj[i].NEW_ADJ_AMT &&
                this.sendobj[i].NEW_ADJUSTMENT_DESC === this.sendobj[i].OLD_ADJUSTMENT_DESC) {
              this.sendobj.splice(i, 1);
              finalRepointList.splice(i, 1);
              i--;
            }
          }
        } else {
          this.addToJson(finalRepointList[i], false);
        }
      }

      const finalSendObj = this.sendobj.concat(this.tempDeletedRepointList);

      // Build jsonSetting exactly like original AngularJS (preserving original types - numbers, not strings)
      // Original: var jsonSetting = {'TAX_YEAR':rowData.TAX_YEAR, 'tax_year':rowData.TAX_YEAR,
      //           'SCENARIO' : GlobalService.globalParams.scenario, 'JCD_KEY' : GlobalService.globalParams.jcd_key};
      const jsonSetting = {
        'TAX_YEAR': this.rowData.TAX_YEAR,
        'tax_year': this.rowData.TAX_YEAR,
        'SCENARIO': this.filterParams?.scenario || this.globalService?.globalParams?.scenario,
        'JCD_KEY': this.filterParams?.jcd_key || this.globalService?.globalParams?.jcd_key
      };

      this.saveTrialBalanceData(jsonSetting, finalSendObj, 'Repoint has been saved successfully');
    } else {
      // Validation failed - show appropriate message
      if (this.showRepointingDescError) {
        this.gtwSnackbarService.openSnackBar('Please add repointing description', 'gtw-snackbar--red');
      } else if (this.acctzeroflag) {
        this.gtwSnackbarService.openSnackBar('Amount cannot be zero', 'gtw-snackbar--red');
      }
      this.crudLoading = false;
      this.cdr.detectChanges();
    }
  }

  private saveTrialBalanceData(jsonSetting: any, sendobj: any[], message: string) {
    this.firRepointService.saveTb(
      this.baseURLs.api,
      jsonSetting,
      sendobj,
      this.rowData.SOURCE_SYSTEM,
      this.globalService?.globalParams || {},
      this.filterParams
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (result: any) => {
        this.crudLoading = false;

        if (result.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          this.is_data_saved = true;

          // Emit refresh event
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('An error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  // Reset method - exact match of original AngularJS implementation
  reset() {
    this.crudLoading = false;

    const epoch = ++this.loadEpoch;

    if (this.repoint_data.length > 0) {
      // Original: vm.sys_acc_lookup_data = []; vm.repoint_data = [];
      // Then calls: getSystemAcctLookUP(); fetchSystemAcctData(); getDefaultSystemAcctValues();
      this.sys_acc_lookup_data = [];
      this.repoint_data = [];
      this.getSystemAcctLookup(epoch);
      this.fetchSystemAcctData(epoch);
      this.getDefaultSystemAcctValues(epoch);
    } else {
      // Original: vm.repointList = []; vm.sys_acc_lookup_data = []; vm.repoint_data = [];
      // vm.Repoint.ADJUSTMENT_DESC = "";
      // Then calls: getSystemAcctLookUP(); fetchSystemAcctData(); getDefaultSystemAcctValues(); vm.repointAmt();
      this.repointList = [];
      this.sys_acc_lookup_data = [];
      this.repoint_data = [];
      this.Repoint.ADJUSTMENT_DESC = "";
      this.getSystemAcctLookup(epoch);
      this.fetchSystemAcctData(epoch);
      this.getDefaultSystemAcctValues(epoch);
      this.repointAmt();
    }
  }

  // Cancel method
  cancel() {
    this.sendobj = [];
    this.requestGridUpdate.emit('cancel');
  }

  // API: Fetch System Account Data
  private fetchSystemAcctData(epoch: number) {
    this.firRepointService.fetchSystemAcctData(
      this.baseURLs.api,
      this.rowData.TAX_YEAR,
      this.rowData.LOCAL_ACCT_KEY,
      this.rowData.SYSTEM_ACCT_KEY,
      this.globalService?.globalParams || {}
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any) => {
        if (epoch !== this.loadEpoch) {
          return;
        }
        if (data.callSuccess === "1") {
          this.loadSystemAndLocalAccountData = data.jsonObject || [];
          this.processSystemAccountData();
        } else {
          this.handleApiError(data);
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (epoch !== this.loadEpoch) {
          return;
        }
        console.error('Error fetching system account data:', error);
        this.cdr.detectChanges();
      }
    );
  }

  private processSystemAccountData() {
    // Handle special cases for SYSTEM_ACCT_TYPE 6 or 7
    if (this.rowData.SYSTEM_ACCT_TYPE === 6 || this.rowData.SYSTEM_ACCT_TYPE === 7) {
      let form5471_m_count = 0;
      let form8858_m_count = 0;
      let form8865_n_count = 0;

      for (let i = 0; i < this.loadSystemAndLocalAccountData.length; i++) {
        const item = this.loadSystemAndLocalAccountData[i];

        if (item.FORM === '5471' && item.SCHEDULE === 'M') {
          form5471_m_count++;
        }

        if (item.FORM === '8858' && item.SCHEDULE === 'M') {
          form8858_m_count++;

          if (item.COL && item.COL.split(",").length > 1) {
            const colParts = item.COL.split(",");
            if (this.rowData.TAX_TYPE === 'BRUS') {
              item.COL = colParts[0].includes('_US') ? colParts[0] : colParts[1];
            } else {
              item.COL = colParts[0].includes('_US') ? colParts[1] : colParts[0];
            }
          }
        }

        if (item.FORM === '8865' && item.SCHEDULE === 'N') {
          form8865_n_count++;
        }
      }

      // Add missing entries
      if (form5471_m_count === 0) {
        this.loadSystemAndLocalAccountData.push({
          ROW_ORDER: "1",
          SYS_ACCT_KEY: this.rowData.SYSTEM_ACCT_KEY,
          FORMS: "5471",
          FORM: "5471",
          SCHEDULE: "M",
          LINE: "",
          LINE_DESC: "",
          COL: "",
          FROM_CHART_KEY: null
        });
      }

      if (form8858_m_count === 0) {
        this.loadSystemAndLocalAccountData.push({
          ROW_ORDER: "2",
          SYS_ACCT_KEY: this.rowData.SYSTEM_ACCT_KEY,
          FORMS: "8858",
          FORM: "8858",
          SCHEDULE: "M",
          LINE: "",
          LINE_DESC: "",
          COL: "",
          FROM_CHART_KEY: null
        });
      }

      if (form8865_n_count === 0) {
        this.loadSystemAndLocalAccountData.push({
          ROW_ORDER: "3",
          SYS_ACCT_KEY: this.rowData.SYSTEM_ACCT_KEY,
          FORMS: "8865",
          FORM: "8865",
          SCHEDULE: "N",
          LINE: "",
          LINE_DESC: "",
          COL: "",
          FROM_CHART_KEY: null
        });
      }
    }
  }

  // API: Get Default System Account Values
  private getDefaultSystemAcctValues(epoch: number) {
    // Use globalParams directly (matching legacy: GlobalService.globalParams.scenario/jcd_key/tax_year)
    const gp = this.globalService?.globalParams || {};
    this.firRepointService.getDefaultSystemAcctValues(
      this.baseURLs.api,
      gp.scenario || this.filterParams?.scenario,
      gp.jcd_key || this.filterParams?.jcd_key,
      gp.tax_year || this.filterParams?.tax_year,
      this.rowData.SOURCE_SYSTEM,
      this.rowData.COMBINATION_KEY,
      this.rowData.LOCAL_ACCT,
      this.rowData.LOCAL_CHART_KEY,
      gp
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any) => {
        if (epoch !== this.loadEpoch) {
          return;
        }
        if (data.errorMessage === 'null' || !data.errorMessage || data.errorMessage.length === 0) {
          this.repoint_data = data.jsonObject || [];
          this.processRepointData();
        } else {
          this.gtwSnackbarService.openSnackBar(data.errorMessage, 'gtw-snackbar--red');
          // On error, still try to show form if lookup data is available
          if (this.sys_acc_lookup_data && this.sys_acc_lookup_data.length > 0) {
            this.loadLookUpData = true;
          }
        }
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (epoch !== this.loadEpoch) {
          return;
        }
        console.error('Error getting default system account values:', error);
        this.isLoading = false;
        // On error, still try to show form if lookup data is available
        if (this.sys_acc_lookup_data && this.sys_acc_lookup_data.length > 0) {
          this.loadLookUpData = true;
        }
        this.cdr.detectChanges();
      }
    );
  }

  private processRepointData() {
    if (this.repoint_data.length > 0) {
      this.repointList = [];
      let temp_group_key: number | null = null;

      this.repoint_data.forEach((sys_acc_data: any) => {
        this.Repoint.SYSC_ACCT = sys_acc_data.SYS_ACCT;
        this.Repoint.AMMOUNT = Number(sys_acc_data.AMOUNT);
        this.Repoint.ADJUSTMENT_DESC = sys_acc_data.ADJUSTMENT_DESC;
        this.Repoint.SYS_ACCT_KEY = sys_acc_data.SYS_ACCT_KEY;
        this.Repoint.TRANS_HEADER_KEY = sys_acc_data.TRANS_HEADER_KEY;
        this.Repoint.TRANS_DETAILS_KEY = sys_acc_data.TRANS_DETAILS_KEY;
        this.Repoint.IRS_Form = sys_acc_data.FORM;
        this.Repoint.IRS_SCHEDULE = sys_acc_data.SCHEDULE;
        this.Repoint.IRS_LINE = sys_acc_data.LINE || '';
        this.Repoint.CHECK_COL = sys_acc_data.COL;
        // Use LINE_DESC directly from API (matching original AngularJS implementation)
        this.Repoint.IRS_LINE_DESC = sys_acc_data.LINE_DESC || '';
        this.OLD_DESC = this.Repoint.ADJUSTMENT_DESC;
        this.Repoint.REPOINT_ACCT_KEY = sys_acc_data.REPOINT_ACCT_KEY;
        this.Repoint.MAIN_ACCT_Y_N = sys_acc_data.MAIN_ACCT_Y_N;
        this.Repoint.DISABLED = false;
        this.Repoint.DELETE_BTN = temp_group_key !== sys_acc_data.GROUP_KEY;
        this.Repoint.GROUP_KEY = sys_acc_data.GROUP_KEY;
        this.group_key = sys_acc_data.GROUP_KEY;

        temp_group_key = sys_acc_data.GROUP_KEY;

        this.repointList.push({
          SYSC_ACCT: this.Repoint.SYSC_ACCT,
          SYS_ACCT_KEY: this.Repoint.SYS_ACCT_KEY,
          TRANS_HEADER_KEY: this.Repoint.TRANS_HEADER_KEY,
          TRANS_DETAILS_KEY: this.Repoint.TRANS_DETAILS_KEY,
          IRS_Form: this.Repoint.IRS_Form,
          IRS_SCHEDULE: this.Repoint.IRS_SCHEDULE,
          IRS_LINE: this.Repoint.IRS_LINE,
          CHECK_COL: this.Repoint.CHECK_COL,
          IRS_LINE_DESC: this.Repoint.IRS_LINE_DESC,
          LOOK_UP_BTN: null,
          AMMOUNT: this.Repoint.AMMOUNT,
          REPOINT_ACCT_KEY: this.Repoint.REPOINT_ACCT_KEY,
          MAIN_ACCT_Y_N: this.Repoint.MAIN_ACCT_Y_N,
          DISABLED: this.Repoint.DISABLED,
          DELETE_BTN: this.Repoint.DELETE_BTN,
          GROUP_KEY: this.Repoint.GROUP_KEY
        });

        this.addToJson(this.Repoint, true);
      });

      // Reset Repoint object
      this.Repoint.SYSC_ACCT = '';
      this.Repoint.AMMOUNT = '';
      this.Repoint.SYS_ACCT_KEY = '';
      this.Repoint.TRANS_HEADER_KEY = '';
      this.Repoint.IRS_Form = '';
      this.Repoint.IRS_SCHEDULE = '';
      this.Repoint.CHECK_COL = '';
      this.Repoint.IRS_LINE = '';
      this.Repoint.IRS_LINE_DESC = '';

      this.repointAmt();
      
      // Update lookup data for repointList items if lookup data is already available
      // This handles the race condition where getSystemAcctLookup() completes before this
      this.updateRepointListLookupData();
    } else {
      // No saved repoint data - still need to show the form (hide spinner)
      // If lookup data is already loaded, enable the form
      if (this.sys_acc_lookup_data && this.sys_acc_lookup_data.length > 0) {
        this.loadLookUpData = true;
        this.cdr.detectChanges();
      }
    }
  }

  // Update lookup dropdown data for all items in repointList
  private updateRepointListLookupData() {
    // Check if lookup data is available
    if (this.sys_acc_lookup_data && this.sys_acc_lookup_data.length > 0) {
      
      // If there are repoint items, update their lookup dropdowns
      if (this.repointList.length > 0) {
        const lookupData = _.cloneDeep(this.sys_acc_lookup_data);
        for (const repoint of this.repointList) {
          repoint.loadLookupSchedule = this.filterDataByKey("FORM", 'SCHEDULE', lookupData, repoint.IRS_Form, '');
          repoint.loadLookupLineDesc = this.filterDataByKey("SCHEDULE", 'LINE_DESC', lookupData, repoint.IRS_SCHEDULE, repoint.IRS_Form);
          
          // Match IRS_LINE_DESC to available options if there's a format mismatch
          // The lookup data has LINE_DESC in format "LINE - DESCRIPTION" (e.g., "2A - Trade notes...")
          // But saved data might have just LINE_DESC without the LINE prefix
          if (repoint.loadLookupLineDesc && repoint.loadLookupLineDesc.length > 0) {
            // Check if current value is already in the options (exact match)
            if (repoint.IRS_LINE_DESC && !repoint.loadLookupLineDesc.includes(repoint.IRS_LINE_DESC)) {
              // Try to find a matching option
              const irsLine = repoint.IRS_LINE || '';
              const irsLineDesc = repoint.IRS_LINE_DESC || '';
              
              const matchedOption = repoint.loadLookupLineDesc.find((option: string) => {
                // 1. Match by LINE number prefix (e.g., "2A" matches "2A - Description")
                if (irsLine && option.startsWith(irsLine + " - ")) {
                  return true;
                }
                // 2. Match if option starts with LINE followed by space
                if (irsLine && option.startsWith(irsLine + " ")) {
                  return true;
                }
                // 3. Match if LINE_DESC is contained in the option (after the " - ")
                if (irsLineDesc && option.includes(" - " + irsLineDesc)) {
                  return true;
                }
                // 4. Match if LINE_DESC is contained anywhere in the option
                if (irsLineDesc && option.toLowerCase().includes(irsLineDesc.toLowerCase())) {
                  return true;
                }
                return false;
              });
              
              if (matchedOption) {
                repoint.IRS_LINE_DESC = matchedOption;
              }
            } else if (!repoint.IRS_LINE_DESC && repoint.IRS_LINE) {
              // If no LINE_DESC but we have LINE, try to find matching option by LINE
              const matchedOption = repoint.loadLookupLineDesc.find((option: string) => 
                option.startsWith(repoint.IRS_LINE + " - ") || option.startsWith(repoint.IRS_LINE + " ")
              );
              if (matchedOption) {
                repoint.IRS_LINE_DESC = matchedOption;
              }
            }
          }
          
          const lookupCols = this.filterDataByKey("LINE_DESC", 'COL', lookupData, repoint.IRS_LINE_DESC, repoint.IRS_SCHEDULE);
          const accountCols = this.collectAccountColsForRepoint(repoint);
          repoint.loadLookupColumn = this.buildColumnOptions(lookupCols, accountCols, repoint.CHECK_COL);
        }
      }
      
      // Hide spinner - lookup data is ready (even if repointList is empty - user can add new items)
      this.loadLookUpData = true;
      this.cdr.detectChanges();
    }
  }

  // API: Get System Account Lookup
  private getSystemAcctLookup(epoch: number) {
    this.firRepointService.getSystemAcctLookup(
      this.baseURLs.api,
      this.rowData.SCREEN_KEY,
      this.rowData.SYSTEM_ACCT_TYPE,
      this.globalService?.globalParams || {}
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe(
      (data: any) => {
        if (epoch !== this.loadEpoch) {
          return;
        }
        if (data.errorMessage === 'null' || !data.errorMessage || data.errorMessage.length === 0) {
          this.sys_acc_lookup_data = data.jsonObject || [];

          // Build loadLookupForm from lookup data
          for (const item of data.jsonObject) {
            const index = this.loadSystemAndLocalAccountData.findIndex(e => e.FORM === item.FORM);
            if (index !== -1) {
              this.loadLookupForm.push(item.FORM);
            }
          }
          this.loadLookupForm = _.uniq(this.loadLookupForm);

          // Update repointList with lookup data (handles race condition)
          this.updateRepointListLookupData();
        } else {
          this.gtwSnackbarService.openSnackBar(data.errorMessage, 'gtw-snackbar--red');
          // Even on error, hide spinner so user can see the form
          this.loadLookUpData = true;
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (epoch !== this.loadEpoch) {
          return;
        }
        console.error('Error getting system account lookup:', error);
        // On error, still hide spinner so user can see the form
        this.loadLookUpData = true;
        this.cdr.detectChanges();
      }
    );
  }

  private handleApiError(data: any) {
    if (data.errorMessage === "no access") {
      this.gtwSnackbarService.openSnackBar('Sorry you do not have access to do the requested action.', 'gtw-snackbar--red');
    } else {
      this.gtwSnackbarService.openSnackBar('An error has occurred, If you continue to experience this issue please visit the help page to contact support.', 'gtw-snackbar--red');
    }
  }

  // Track by function for ngFor
  trackByIndex(index: number): number {
    return index;
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.loadEpoch++;
    this.contextSubs.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}

